import { secureFetch } from './auth'
import { HttpError, failureMessage } from './orders-api'

// R142 (B) — the four lookups the HOP New Order pickers call: by-phone find, hop
// find-or-create, the products lookup, and the public delivery zones list. All four
// already admit hop server-side (R146b, and the products/zones endpoints pre-dating it);
// this is only the client that reaches them.

export interface FoundCustomer {
  id: string
  name: string | null
  lastZoneId: string | null
}

export interface HopCustomer {
  id: string
  name: string | null
  lastZoneId: string | null
  created: boolean
}

export interface LookupVariant {
  id: string
  productId: string
  nameEn?: string
  nameAr?: string
  unitOfMeasure?: string
  packSize?: string
}

export interface LookupProduct {
  id: string
  nameEn?: string
  nameAr?: string
  slug?: string
  price: string
  variants: LookupVariant[]
}

export interface DeliveryLocation {
  id: string
  cityNameEn: string
  cityNameAr: string
  deliveryFee: string
  sameDay: boolean
}

export interface DeliveryZone {
  id: string
  slug: string
  nameEn: string
  nameAr: string
  locations: DeliveryLocation[]
}

export async function findCustomersByPhone(phone: string): Promise<FoundCustomer[]> {
  const res = await secureFetch(`/api/admin/customers/by-phone?phone=${encodeURIComponent(phone)}`)
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  const data = (await res.json()) as { items: FoundCustomer[] }
  return data.items
}

// R485 client half (Dee via Rook #66417, #66468): the customers whose stored number STARTS with
// what was typed, at most 20 by last order, and `total`, the full match count. The server waits
// (an empty list) on fewer than 2 national digits and on a bare prefix of 961; 429 when one
// login searches too often (PHONE_SEARCH_RATE_LIMITED); 400 for text that is no number.
export interface SearchedCustomer {
  id: string
  name: string | null
  phone: string | null
}

export interface CustomerSearchResult {
  items: SearchedCustomer[]
  total: number
}

export async function searchCustomersByPhone(phone: string): Promise<CustomerSearchResult> {
  const res = await secureFetch(`/api/admin/customers/search?phone=${encodeURIComponent(phone)}`)
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<CustomerSearchResult>
}

export async function hopFindOrCreateCustomer(input: { phone: string; name?: string }): Promise<HopCustomer> {
  const body: { phone: string; name?: string } = { phone: input.phone }
  if (input.name?.trim()) body.name = input.name.trim()
  const res = await secureFetch('/api/admin/customers/hop', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<HopCustomer>
}

export async function searchProducts(q: string): Promise<LookupProduct[]> {
  const res = await secureFetch(`/api/products-lookup?q=${encodeURIComponent(q)}`)
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<LookupProduct[]>
}

export async function fetchDeliveryZones(): Promise<DeliveryZone[]> {
  const res = await secureFetch('/api/public/delivery-zones')
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<DeliveryZone[]>
}
